import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSmartNavigate } from '@/lib/nav-prefs/use-smart-navigate';
import { useTranslation } from 'react-i18next';
import {
  useRepresentative,
  useRepresentativeClients,
} from '@/hooks/use-representatives';
import { useClients } from '@/hooks/use-clients';
import { useSalesDocuments } from '@/hooks/use-sales';
import { usePayments } from '@/hooks/use-payments';
import {
  calculateRepresentativeStats,
  calculateRepresentativeWalletBalance,
} from '@erp/domain/representatives';
import type { Client, SalesDocument } from '@erp/data-access/interfaces';
import { RepresentativeFormDialog } from './RepresentativeFormDialog';
import {
  Card, CardContent, Badge, Button, Separator,
  Tabs, TabsList, TabsTrigger, TabsContent,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  DatePicker, FilterBar, FilterField, Label, cn,
} from '@erp/ui';
import { ArrowRight, Pencil } from '@/components/icons';
import { SearchableSelect } from '@/components/ui/searchable-select';

function formatPrice(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}
function formatDate(d: string): string {
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

const DOC_TYPE_LABELS: Record<string, string> = {
  quote: 'sales.quote', order: 'sales.order', delivery: 'sales.delivery',
  invoice: 'sales.invoice', credit_note: 'sales.creditNote',
};
const DOC_PATHS: Record<string, string> = {
  quote: '/sales/quotes', order: '/sales/orders', delivery: '/sales/deliveries',
  invoice: '/sales/invoices', credit_note: '/sales/credit-notes',
};
const DOC_TYPES_ORDER = ['quote', 'order', 'delivery', 'invoice', 'credit_note'] as const;

type PeriodKey = 'month' | 'year' | 'all' | 'custom';

function todayISO(): string { return new Date().toISOString().slice(0, 10); }
function startOfMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}
function startOfYear(): string {
  return `${new Date().getFullYear()}-01-01`;
}

export function RepresentativeProfilePage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useSmartNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [period, setPeriod] = useState<PeriodKey>('year');
  const [customFrom, setCustomFrom] = useState<string>(startOfYear());
  const [customTo, setCustomTo] = useState<string>(todayISO());
  const [docTypeFilter, setDocTypeFilter] = useState('');

  const { data: rep, isLoading } = useRepresentative(id ?? '');
  const { data: linkedClients } = useRepresentativeClients(id ?? '');
  const { data: allClients } = useClients();
  const { data: allDocs } = useSalesDocuments({ representativeId: id });
  // All client payments: receipts paying invoices + disbursements refunding
  // credit notes. Filtered by client-side because the payments filter doesn't
  // support representative_id; fine for TPE/PME volumes.
  const { data: allClientPayments } = usePayments({ thirdPartyType: 'client' });

  // "Clients" tab shows the union of (A) clients explicitly linked via M:N and
  // (B) clients that appear on any document attached to this rep — otherwise
  // picking a rep on a one-off doc would never show the client here.
  const relatedClients = useMemo<Client[]>(() => {
    const byId = new Map<string, Client>();
    for (const c of linkedClients ?? []) byId.set(c.id, c);
    if (allClients && allDocs) {
      for (const d of allDocs) {
        if (byId.has(d.clientId)) continue;
        const match = allClients.find((c) => c.id === d.clientId);
        if (match) byId.set(match.id, match);
      }
    }
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [linkedClients, allClients, allDocs]);

  const linkedIds = useMemo(
    () => new Set((linkedClients ?? []).map((c) => c.id)),
    [linkedClients],
  );

  const periodFilter = useMemo((): { from?: string; to?: string } => {
    if (period === 'all') return {};
    if (period === 'month') return { from: startOfMonth(), to: todayISO() };
    if (period === 'year') return { from: startOfYear(), to: todayISO() };
    return { from: customFrom || undefined, to: customTo || undefined };
  }, [period, customFrom, customTo]);

  const filteredDocs = useMemo(() => {
    if (!allDocs) return [];
    const { from, to } = periodFilter;
    return allDocs.filter((d) => {
      if (from && d.docDate < from) return false;
      if (to && d.docDate > to) return false;
      if (docTypeFilter && d.docType !== docTypeFilter) return false;
      return true;
    });
  }, [allDocs, docTypeFilter, periodFilter]);

  const docTypeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    const { from, to } = periodFilter;
    for (const doc of allDocs ?? []) {
      if (from && doc.docDate < from) continue;
      if (to && doc.docDate > to) continue;
      counts[doc.docType] = (counts[doc.docType] ?? 0) + 1;
    }
    return counts;
  }, [allDocs, periodFilter]);
  const docTypeFilterOptions = useMemo(
    () => DOC_TYPES_ORDER
      .filter((type) => (docTypeCounts[type] ?? 0) > 0)
      .map((type) => ({
        value: type,
        label: `${t(DOC_TYPE_LABELS[type]!)} (${docTypeCounts[type] ?? 0})`,
      })),
    [docTypeCounts, t],
  );

  const statsDocs = useMemo(
    () =>
      (allDocs ?? []).map((d: SalesDocument) => ({
        id: d.id,
        docType: d.docType,
        docDate: d.docDate,
        isInvoiced: d.isInvoiced,
        totalHt: d.totalHt,
        totalTtc: d.totalTtc,
        netToPay: d.netToPay,
        parentDocIds: d.parentDocIds,
        lines: (d.lines ?? []).map((l) => ({
          quantity: l.quantity,
          unitPriceHt: l.unitPriceHt,
          purchasePrice: l.purchasePrice,
          discountPct: l.discountPct,
        })),
      })),
    [allDocs],
  );

  const stats = useMemo(
    () => calculateRepresentativeStats(statsDocs, periodFilter),
    [statsDocs, periodFilter],
  );

  const allocations = useMemo(
    () =>
      (allClientPayments ?? []).flatMap((p) =>
        (p.allocations ?? []).map((a) => ({
          documentId: a.documentId,
          allocatedAmount: a.allocatedAmount,
        })),
      ),
    [allClientPayments],
  );

  const walletBalance = useMemo(
    () =>
      calculateRepresentativeWalletBalance(
        statsDocs,
        allocations,
        rep?.commissionRate ?? 0,
        periodFilter,
      ),
    [statsDocs, allocations, rep?.commissionRate, periodFilter],
  );

  if (isLoading && !rep) {
    return <div className="flex items-center justify-center py-12 text-muted-foreground">{t('common.loading')}</div>;
  }
  if (!rep) {
    return <div className="flex items-center justify-center py-12 text-muted-foreground">{t('common.noResults')}</div>;
  }

  return (
    <div className="space-y-4">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 -mx-4 px-4 py-3 bg-background/95 backdrop-blur border-b">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-bold truncate">{rep.name}</h2>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {rep.phone && <>{rep.phone}</>}
              {rep.phone && rep.email && ' · '}
              {rep.email && <>{rep.email}</>}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil size={14} className="me-1.5" />
              {t('common.edit')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/representatives')}>
              {t('common.back')}
            </Button>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-3 items-end">
          <div className="flex items-center gap-2 flex-wrap">
            <Label className="text-xs text-muted-foreground whitespace-nowrap">
              {t('representatives.period')}
            </Label>
            <Select value={period} onValueChange={(v) => setPeriod(v as PeriodKey)}>
              <SelectTrigger className="w-44 h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="month">{t('representatives.periods.month')}</SelectItem>
                <SelectItem value="year">{t('representatives.periods.year')}</SelectItem>
                <SelectItem value="all">{t('representatives.periods.all')}</SelectItem>
                <SelectItem value="custom">{t('representatives.periods.custom')}</SelectItem>
              </SelectContent>
            </Select>
            {period === 'custom' && (
              <div className="flex items-center gap-2">
                <DatePicker value={customFrom} onChange={setCustomFrom} />
                <span className="text-muted-foreground text-xs">→</span>
                <DatePicker value={customTo} onChange={setCustomTo} />
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Kpi label={t('representatives.revenue')} value={`${formatPrice(stats.revenue)} DT`} />
            <Kpi
              label={t('representatives.profit')}
              value={`${formatPrice(stats.profit)} DT`}
              positive={stats.profit >= 0}
            />
            <Kpi
              label={`${t('representatives.wallet')} (${rep.commissionRate}%)`}
              value={`${formatPrice(walletBalance)} DT`}
              positive={walletBalance >= 0}
              hint={t('representatives.walletHint')}
            />
            <Kpi label={t('representatives.docCount')} value={String(stats.docCount)} />
          </div>
        </div>
      </div>

      <Tabs defaultValue="documents">
        <TabsList>
          <TabsTrigger value="documents">
            {t('representatives.tabs.documents')} ({filteredDocs.length})
          </TabsTrigger>
          <TabsTrigger value="clients">
            {t('representatives.tabs.clients')} ({relatedClients.length})
          </TabsTrigger>
          <TabsTrigger value="info">{t('representatives.tabs.info')}</TabsTrigger>
        </TabsList>

        <TabsContent value="documents">
          <Card>
            <CardContent className="pt-5">
              {(docTypeFilterOptions.length > 1 || docTypeFilter) && (
                <FilterBar>
                  <FilterField label={t('dashboard.type')} minWidth={220}>
                    <SearchableSelect
                      options={docTypeFilterOptions}
                      value={docTypeFilter}
                      onChange={setDocTypeFilter}
                      label={t('dashboard.type')}
                      placeholder={t('common.all')}
                      searchPlaceholder={t('common.search')}
                      className="h-8"
                    />
                  </FilterField>
                </FilterBar>
              )}
              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('dashboard.type')}</TableHead>
                      <TableHead>{t('sales.number')}</TableHead>
                      <TableHead>{t('sales.docDate')}</TableHead>
                      <TableHead className="text-end">{t('sales.totalHt')}</TableHead>
                      <TableHead className="w-8" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!filteredDocs.length ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                          {t('common.noResults')}
                        </TableCell>
                      </TableRow>
                    ) : filteredDocs.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell>
                          <Badge variant="outline">{t(DOC_TYPE_LABELS[doc.docType] ?? doc.docType)}</Badge>
                        </TableCell>
                        <TableCell className="font-medium">{doc.number}</TableCell>
                        <TableCell>{formatDate(doc.docDate)}</TableCell>
                        <TableCell className="text-end">{formatPrice(doc.totalHt)} DT</TableCell>
                        <TableCell>
                          <Link to={`${DOC_PATHS[doc.docType]}/${doc.id}`}>
                            <Button variant="ghost" size="icon-xs">
                              <ArrowRight size={14} className="rtl:rotate-180" />
                            </Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="clients">
          <Card>
            <CardContent className="pt-5">
              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('clients.code')}</TableHead>
                      <TableHead>{t('clients.name')}</TableHead>
                      <TableHead>{t('clients.type')}</TableHead>
                      <TableHead>{t('clients.phone')}</TableHead>
                      <TableHead className="w-8" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!relatedClients.length ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                          {t('representatives.noLinkedClients')}
                        </TableCell>
                      </TableRow>
                    ) : relatedClients.map((c) => (
                      <TableRow
                        key={c.id}
                        className="cursor-pointer hover:bg-accent"
                        onClick={() => navigate(`/clients/${c.id}`)}
                      >
                        <TableCell className="font-mono text-xs uppercase text-muted-foreground">
                          {c.code}
                        </TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <span>{c.name}</span>
                            {!linkedIds.has(c.id) && (
                              <Badge variant="outline" className="text-[9px] font-normal text-muted-foreground">
                                {t('representatives.viaDocument')}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={cn(
                              'text-[10px] font-medium',
                              c.type === 'company'
                                ? 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-400'
                                : 'text-muted-foreground',
                            )}
                          >
                            {c.type === 'company' ? t('clients.typeCompany') : t('clients.typeIndividual')}
                          </Badge>
                        </TableCell>
                        <TableCell>{c.phone ?? <span className="text-muted-foreground">—</span>}</TableCell>
                        <TableCell>
                          <ArrowRight size={14} className="rtl:rotate-180 text-muted-foreground" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="info">
          <Card>
            <CardContent className="pt-5 space-y-3">
              <h3 className="text-sm font-semibold">{t('representatives.info')}</h3>
              <div className="space-y-2 text-sm">
                {rep.phone && <InfoRow label={t('representatives.phone')} value={rep.phone} />}
                {rep.email && <InfoRow label={t('representatives.email')} value={rep.email} />}
                <InfoRow
                  label={t('representatives.commissionRate')}
                  value={`${rep.commissionRate}%`}
                />
                {rep.notes && (
                  <>
                    <Separator />
                    <p className="text-muted-foreground whitespace-pre-line">{rep.notes}</p>
                  </>
                )}
                {!rep.phone && !rep.email && !rep.notes && rep.commissionRate === 0 && (
                  <p className="text-muted-foreground">{t('representatives.noExtraInfo')}</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <RepresentativeFormDialog open={editOpen} onOpenChange={setEditOpen} representative={rep} />
    </div>
  );
}

function Kpi({
  label, value, positive, hint,
}: { label: string; value: string; positive?: boolean; hint?: string }) {
  const tone = positive === false ? 'text-destructive' : positive === true ? 'text-green-600' : '';
  return (
    <div className="rounded-md border bg-card px-3 py-2" title={hint}>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`text-base font-bold leading-tight mt-0.5 ${tone}`}>{value}</p>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-end">{value}</span>
    </div>
  );
}
